import { CheckIcon } from '@heroicons/react/24/solid'
import { useMemo } from 'react'

import { Carriers, type EngineLayer } from '@freightview/carriers/ts'

import { matchesCarrierSearch } from './matchesCarrierSearch.js'

export const SUPPORT_LAYERS: { key: EngineLayer; label: string }[] = [
  { key: 'provisioning', label: 'Provisioning' },
  { key: 'rating', label: 'Rating' },
  { key: 'bol', label: 'BOL' },
  { key: 'pickup', label: 'Pickup' },
  { key: 'cancel', label: 'Cancel' },
  { key: 'profinder', label: 'Profinder' },
  { key: 'tracking', label: 'Tracking' },
  { key: 'documents', label: 'Documents' },
  { key: 'invoice', label: 'Invoices' },
]

export interface CarrierSupport {
  name: string
  scac: string
  support: Record<EngineLayer, boolean>
}

export const carrierSupport: CarrierSupport[] = Object.values(Carriers)
  .map(carrier => ({
    name: carrier.name,
    scac: carrier.scac,
    support: SUPPORT_LAYERS.reduce(
      (acc, { key }) => {
        acc[key] = carrier.engineLayers.includes(key)
        return acc
      },
      {} as Record<EngineLayer, boolean>,
    ),
  }))
  .sort((a, b) => a.name.localeCompare(b.name))

export function EngineLayersTable({ search }: { search: string }) {
  const carriers = useMemo(
    () =>
      carrierSupport.filter(carrier => matchesCarrierSearch(carrier, search)),
    [search],
  )

  const totals = SUPPORT_LAYERS.map(({ key, label }) => ({
    key,
    label,
    count: carrierSupport.filter(carrier => carrier.support[key]).length,
  }))

  return (
    <div className="overflow-auto rounded-box border border-base-300 bg-base-100">
      <table className="table table-sm table-pin-rows">
        <thead>
          <tr>
            <th className="bg-base-100">Carrier</th>
            <th className="bg-base-100">SCAC</th>
            {SUPPORT_LAYERS.map(({ key, label }) => (
              <th key={key} className="bg-base-100 text-center">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {carriers.map(carrier => (
            <tr key={carrier.scac} className="hover">
              <td className="font-medium whitespace-nowrap">{carrier.name}</td>
              <td className="font-mono uppercase opacity-70">{carrier.scac}</td>
              {SUPPORT_LAYERS.map(({ key, label }) => (
                <td key={key} className="text-center">
                  {carrier.support[key] ? (
                    <CheckIcon
                      className="mx-auto h-4 w-4 text-success"
                      aria-label={`${label} supported`}
                    />
                  ) : (
                    <span className="sr-only">Not supported</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th className="bg-base-100">Totals</th>
            <th className="bg-base-100" />
            {totals.map(({ key, count }) => (
              <th key={key} className="bg-base-100 text-center">
                {count}
              </th>
            ))}
          </tr>
        </tfoot>
      </table>
      {carriers.length === 0 ? (
        <div className="p-8 text-center text-sm opacity-70">
          No carriers match your search.
        </div>
      ) : null}
    </div>
  )
}
